/**
 * `/favorites` — TASK 9.2 Steps 2-3.
 *
 * The mock's HEARTED screen (mock/SteamyMusic.dc.html L387-437): a SONGS
 * section (rows + the ▶ PLAY ALL / ⤨ SHUFFLE / + QUEUE collection controls,
 * AC 10.2) above an ALBUMS cover grid, wired to `GET /api/favorites`
 * (TASK 6.1) instead of `db.favTracks`/`db.favAlbums`.
 *
 * ── THE TWO SECTIONS ARE INDEPENDENT SURFACES (dto.ts favoritesQuerySchema) ─
 * The endpoint pages Songs and Albums SEPARATELY: the first (no-`type`)
 * request returns one page of each, and every later request — a section's
 * Retry, a section's next page — is `type`-narrowed, because a `cursor`
 * without a `type` is a 422. A response section can therefore be ABSENT
 * ("not asked for"), which is NOT the same as empty ("the user has no
 * favorites"): an absent section this view DID ask for is a server contract
 * violation, reported through the diagnostics seam and rendered as a
 * retriable error — never as "no favorites yet" for data that was simply
 * excluded.
 *
 * ── BOUNDED ON BOTH AXES (AC 4.3 / W4) ────────────────────────────────────
 * Each section renders through the shared VirtualList: at most one page
 * (≤ 200, the server's own clamp) is FETCHED at a time, and at most the
 * render window is IN THE DOM whatever the length of the loaded array — the
 * bench profile's large favorites set costs the same DOM as a small one.
 * Each section's "Show more" widens or pages ONLY that section.
 *
 * ── AC 10.3: UNFAVORITE REMOVES THE ROW IMMEDIATELY ───────────────────────
 * The heart runs the shared optimistic toggle (components/FavoriteToggle.js),
 * which writes the store synchronously; this view's store subscription diffs
 * the favourite id arrays BY REFERENCE (the transport patches `playback.pos`
 * ~4×/s — those ticks must cost a comparison, not a repaint) and hides
 * exactly the rows whose id left the set. A failed persist re-adds the id
 * (the toggle's rollback), and the SAME diff restores the row — apply and
 * revert travel one channel. The loaded entries are kept; only visibility
 * changes, so a rollback needs no refetch.
 *
 * On load the view MERGES the fetched favourite ids into the store: the
 * server's favorites read is authoritative about what is favorited, and every
 * surface (including the shared toggle's add-vs-remove decision) reads that
 * flag from `catalog.favorites`. Merge, not replace — ids favorited from
 * other surfaces this session must survive a favorites-page visit.
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside TASK 9.2's files */

import { TESTIDS, actionsForButton, playTrackButton } from '@streamymusic/shared';

import {
  AVAILABILITY_COPY,
  AvailabilityBadge,
  availabilityOpacity,
  availabilityState,
  isPlayable,
} from '../../components/AvailabilityBadge.js';
import { FavoriteToggle } from '../../components/FavoriteToggle.js';
import { VirtualList } from '../../components/VirtualList.js';
import { ErrorBlock, LoadingBlock } from '../../components/StateBlocks.js';
import { append, el } from '../../components/primitives/dom.js';
import { formatSeconds } from '../../components/primitives/format.js';
import { AlbumGridCard } from '../browse/ArtistDetail.js';

/**
 * Per-section page size AND render window. 200 is the server's own clamp
 * (dto.ts `PAGE_SIZE_MAX`) and ui-contract §10.6's cap for ordered lists —
 * the fetch bound and the DOM bound deliberately coincide (AC 4.3 / W4).
 */
export const FAVORITES_PAGE_SIZE = 200;

/** mock L414 — the Songs empty strip. */
export const FAVORITES_SONGS_EMPTY_MESSAGE = 'No favorite songs yet — tap the ♥ on any track.';
/** mock L434 — the Albums empty strip. */
export const FAVORITES_ALBUMS_EMPTY_MESSAGE = 'No favorite albums yet — ♥ an album from its page.';

/**
 * @typedef {object} FavoritesHandlers
 * @property {(tracks: import('@streamymusic/shared').TrackCard[]) => void} onPlayAll
 *   PLAYABLE tracks only, in list order (AC 10.2)
 * @property {(tracks: import('@streamymusic/shared').TrackCard[]) => void} onShuffle
 * @property {(tracks: import('@streamymusic/shared').TrackCard[]) => void} onQueue
 * @property {(track: import('@streamymusic/shared').TrackCard,
 *   tracks: import('@streamymusic/shared').TrackCard[]) => void} onPlayTrack
 *   a row tap — the tapped track plus the playable collection it sits in
 * @property {(track: import('@streamymusic/shared').TrackCard) => void} onToggleFavoriteTrack
 *   the ♥ — the composition root routes this into the shared optimistic
 *   toggle (components/FavoriteToggle.js#createFavoriteToggle)
 * @property {(track: import('@streamymusic/shared').TrackCard) => void} onTrackActions
 * @property {(album: import('@streamymusic/shared').AlbumCard) => void} onAlbumActions
 * @property {(message: string, context: { state: string, trackId: string }) => void} onBlocked
 *   non-playable tap (§10.6 toast copy); `context` carries the refused item —
 *   see AlbumDetail's handler docs for why the id is load-bearing (TASK 10.1).
 */

/**
 * Builds the `/favorites` section and starts loading both of its sections.
 *
 * @param {{
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'listFavorites'>,
 *   heading: HTMLElement,
 *   handlers: FavoritesHandlers,
 *   getState: () => import('../../app/state-store.js').AppState,
 *   setState: (patch: Partial<import('../../app/state-store.js').AppState>
 *     | ((current: import('../../app/state-store.js').AppState) => Partial<import('../../app/state-store.js').AppState>)) => unknown,
 *   subscribe: (listener: (next: import('../../app/state-store.js').AppState) => void) => () => void,
 *   offline?: boolean,
 *   downloadedAlbums?: readonly string[],
 *   pageSize?: number,
 *   announce?: (text: string) => void,
 *   warn?: (message: string, detail: Record<string, string>) => void,
 * }} options
 *   `heading` is the route's `<h1 data-testid="page-heading">` — the shell
 *   contract requires exactly one per route, so the view is handed it.
 *   `setState` is the store's write seam, used ONLY to merge the fetched
 *   favourite ids into `catalog.favorites` (see the module header) — every
 *   mutation still travels through `handlers`. `warn` is the app's
 *   diagnostics seam for server contract violations (an absent section, an
 *   `available=false` row where the server promised to filter).
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function FavoritesView(options) {
  const {
    api,
    heading,
    handlers,
    getState,
    setState,
    subscribe,
    offline = false,
    downloadedAlbums = [],
    pageSize = FAVORITES_PAGE_SIZE,
    announce,
    warn = (message) => {
      console.warn(message);
    },
  } = options;

  const section = el('section', { attrs: { 'data-screen-label': 'Favorites' } });

  /* ── skeleton, built once — only the bodies swap ─────────────────────── */

  const songsCount = el('span', { className: 'sm-section-title__count' });
  const songsTitle = el('h2', {
    className: 'sm-section-title sm-section-title--24 sm-section-title--pink',
  });
  append(songsTitle, 'SONGS ', songsCount);

  const songsBody = el('div');
  /** Holds a FAILED "show more" — the loaded rows must survive it. */
  const songsNotice = el('div');
  // The wrapper carries the mock's 24px clearance between the two sections
  // whatever state the songs body is in (rows, skeleton, empty, error).
  const songsRegion = el('div', { className: 'sm-fav-songs' });
  append(songsRegion, songsBody, songsNotice);

  const albumsCount = el('span', { className: 'sm-section-title__count' });
  const albumsTitle = el('h2', {
    className: 'sm-section-title sm-section-title--24 sm-section-title--yellow',
  });
  append(albumsTitle, 'ALBUMS ', albumsCount);

  const albumsBody = el('div');
  const albumsNotice = el('div');

  append(section, heading, songsTitle, songsRegion, albumsTitle, albumsBody, albumsNotice);

  const downloads = new Set(downloadedAlbums);

  /* ── lifecycle guards — one set PER SECTION, they page independently ──── */

  let disposed = false;
  let songsGeneration = 0;
  let albumsGeneration = 0;
  /** @type {AbortController|null} */
  let songsController = null;
  /** @type {AbortController|null} */
  let albumsController = null;

  /** @type {import('@streamymusic/shared').FavoriteTrackEntry[]} */
  let songEntries = [];
  /** @type {string|null} */
  let songsCursor = null;
  /** @type {import('@streamymusic/shared').FavoriteAlbumEntry[]} */
  let albumEntries = [];
  /** @type {string|null} */
  let albumsCursor = null;

  /**
   * Ids optimistically unfavorited THIS visit (AC 10.3). Loaded entries are
   * kept and only hidden, so the toggle's rollback can restore a row without
   * a refetch. Cleared on every fresh (non-append) section load.
   */
  /** @type {Set<string>} */
  const unfavoritedTracks = new Set();
  /** @type {Set<string>} */
  const unfavoritedAlbums = new Set();

  /* ── availability (retain surface — AC 3.9) ───────────────────────────── */

  /**
   * @param {import('@streamymusic/shared').TrackCard} track
   * @returns {import('../../components/AvailabilityBadge.js').AvailabilityState|null}
   */
  function stateOfTrack(track) {
    return availabilityState({
      available: track.available,
      formatSupported: track.formatSupported,
      offline,
      downloaded: downloads.has(track.albumId),
      kind: 'track',
      // Favorites RETAIN a vanished track and mark it "⊘ no longer
      // available" (dto.ts: "render the flag, do not filter").
      surface: 'favorites',
      onScopeViolation: (message) => warn(message, { surface: 'favorites', trackId: track.id }),
    });
  }

  /** The playable slice of the VISIBLE songs, in list order (AC 10.2). */
  function playableSongs() {
    return visibleSongEntries()
      .map((entry) => entry.track)
      .filter((track) => isPlayable(stateOfTrack(track)));
  }

  /* ── SONGS section ────────────────────────────────────────────────────── */

  const actionsRow = el('div', { className: 'sm-actions sm-actions--mb' });
  append(
    actionsRow,
    el('button', {
      className: 'sm-btn sm-btn--primary sm-btn--display sm-btn--display-17',
      text: '▶ PLAY ALL',
      testId: TESTIDS.PLAY_ALL_BUTTON,
      attrs: { type: 'button' },
      on: { click: () => handlers.onPlayAll(playableSongs()) },
    }),
    el('button', {
      className: 'sm-btn sm-btn--accent sm-btn--display sm-btn--display-17',
      text: '⤨ SHUFFLE',
      testId: TESTIDS.SHUFFLE_BUTTON,
      attrs: { type: 'button' },
      on: { click: () => handlers.onShuffle(playableSongs()) },
    }),
    el('button', {
      className: 'sm-btn sm-btn--panel sm-btn--display sm-btn--display-17',
      text: '+ QUEUE',
      testId: TESTIDS.QUEUE_BUTTON,
      attrs: { type: 'button' },
      on: { click: () => handlers.onQueue(playableSongs()) },
    }),
  );

  /**
   * One favorites song row (mock L398-410).
   *
   * @param {import('@streamymusic/shared').FavoriteTrackEntry} entry
   * @returns {HTMLElement}
   */
  function SongRow(entry) {
    const track = entry.track;
    const marker = stateOfTrack(track);

    const row = el('div', {
      className: 'sm-track-row',
      styleProps: { opacity: String(availabilityOpacity(marker)) },
    });

    const textCell = el('span', { className: 'sm-flex-fill' });
    append(
      textCell,
      el('span', { className: 'sm-line-primary', text: track.title }),
      el('span', {
        className: 'sm-line-secondary',
        text: `${track.artistName} · ${track.albumTitle}`,
      }),
      marker === null ? null : AvailabilityBadge({ state: marker }),
    );

    const play = el('button', {
      className: 'sm-track-row__play',
      testId: playTrackButton(track.title),
      attrs: { type: 'button', 'aria-label': `Play ${track.title}` },
      on: {
        click: () => {
          if (marker !== null) {
            // Never start a stream that cannot work — §10.6 per-state copy.
            handlers.onBlocked(AVAILABILITY_COPY[marker].banner, {
              state: marker,
              trackId: track.id,
            });
            return;
          }
          handlers.onPlayTrack(track, playableSongs());
        },
      },
    });
    append(
      play,
      textCell,
      el('span', { className: 'sm-line-trailing', text: formatSeconds(track.durationS) }),
    );

    // Always favorited at paint time — this IS the favorites list. The shared
    // component still owns the aria/testid contract, and an optimistic
    // removal hides the whole row (AC 10.3) rather than unpressing the heart.
    const heart = FavoriteToggle({
      title: track.title,
      favorited: true,
      onToggle: () => handlers.onToggleFavoriteTrack(track),
    });

    append(
      row,
      play,
      heart.element,
      el('button', {
        className: 'sm-btn sm-btn--icon sm-btn--icon-muted',
        text: '⋯',
        testId: actionsForButton(track.title),
        attrs: { type: 'button', 'aria-label': `Actions for ${track.title}` },
        on: { click: () => handlers.onTrackActions(track) },
      }),
    );
    return row;
  }

  const songList = VirtualList({
    items: [],
    windowSize: pageSize,
    renderItem: (entry) => SongRow(entry),
    // An ordered list takes §10.6's "Show more (N more)" affordance.
    loadMoreLabel: ({ remaining }) => `Show more (${remaining} more)`,
    listClassName: 'sm-stack-6',
    onLoadMore: () => {
      void loadSongs({ append: true });
    },
  });

  /** Actions + rows, built once; `songsBody` swaps this in and out whole. */
  const songsWrap = el('div');
  append(songsWrap, actionsRow, songList.element);

  function visibleSongEntries() {
    return songEntries.filter((entry) => !unfavoritedTracks.has(entry.track.id));
  }

  /** @param {number} n */
  function paintSongsCount(n) {
    songsCount.textContent = `(${n.toLocaleString()}${songsCursor === null ? '' : '+'})`;
  }

  /** @param {{ announceEmpty?: boolean }} [opts] */
  function paintSongs(opts = {}) {
    const visible = visibleSongEntries();
    paintSongsCount(visible.length);
    if (visible.length === 0) {
      if (opts.announceEmpty === true && announce !== undefined) {
        announce(FAVORITES_SONGS_EMPTY_MESSAGE);
      }
      songsBody.replaceChildren(SectionEmpty(FAVORITES_SONGS_EMPTY_MESSAGE));
      return;
    }
    songList.update({ items: visible, hasMore: songsCursor !== null });
    if (songsBody.firstChild !== songsWrap) songsBody.replaceChildren(songsWrap);
  }

  /* ── ALBUMS section ───────────────────────────────────────────────────── */

  const albumList = VirtualList({
    items: [],
    windowSize: pageSize,
    renderItem: (entry) =>
      AlbumGridCard({
        album: entry.album,
        // A retain surface: a vanished favorited album keeps its ⊘ ribbon.
        surface: 'favorites',
        favorite: true,
        offline,
        downloaded: downloads.has(entry.album.id),
        onMenu: (album) => handlers.onAlbumActions(album),
        onContractViolation: warn,
      }),
    loadMoreLabel: 'Load more ↓',
    listClassName: 'sm-grid-covers',
    onLoadMore: () => {
      void loadAlbums({ append: true });
    },
  });

  function visibleAlbumEntries() {
    return albumEntries.filter((entry) => !unfavoritedAlbums.has(entry.album.id));
  }

  /** @param {number} n */
  function paintAlbumsCount(n) {
    albumsCount.textContent = `(${n.toLocaleString()}${albumsCursor === null ? '' : '+'})`;
  }

  /** @param {{ announceEmpty?: boolean }} [opts] */
  function paintAlbums(opts = {}) {
    const visible = visibleAlbumEntries();
    paintAlbumsCount(visible.length);
    if (visible.length === 0) {
      if (opts.announceEmpty === true && announce !== undefined) {
        announce(FAVORITES_ALBUMS_EMPTY_MESSAGE);
      }
      albumsBody.replaceChildren(SectionEmpty(FAVORITES_ALBUMS_EMPTY_MESSAGE));
      return;
    }
    albumList.update({ items: visible, hasMore: albumsCursor !== null });
    if (albumsBody.firstChild !== albumList.element) albumsBody.replaceChildren(albumList.element);
  }

  /* ── shared section chrome ────────────────────────────────────────────── */

  /**
   * The mock's per-section dashed empty strip (L414/L434) — deliberately NOT
   * StateBlocks#EmptyBlock: that block is the whole-page "NOTHING ON THIS
   * WALL YET" card, while the favorites sections keep the mock's inline,
   * headline-less strip (`.sm-empty--inline-18` was extracted for exactly
   * these two lines). Announcement still goes through the shell's persistent
   * live region, from the load path.
   *
   * @param {string} message
   * @returns {HTMLElement}
   */
  function SectionEmpty(message) {
    return el('div', {
      className: 'sm-empty sm-empty--inline sm-empty--inline-18',
      text: message,
      attrs: { role: 'status' },
    });
  }

  /**
   * Merges server-confirmed favourite ids into the store — see the module
   * header. A no-op patch is avoided entirely so subscribers never fire for
   * nothing.
   *
   * @param {'tracks'|'albums'} key
   * @param {string[]} ids
   */
  function mergeStoreFavorites(key, ids) {
    const known = new Set(getState().catalog.favorites[key]);
    const missing = ids.filter((id) => !known.has(id));
    if (missing.length === 0) return;
    setState((state) => ({
      catalog: {
        ...state.catalog,
        favorites: {
          ...state.catalog.favorites,
          [key]: [...state.catalog.favorites[key], ...missing],
        },
      },
    }));
  }

  /* ── store subscription (C9 discipline) ───────────────────────────────── */

  /** What the last repaint was painted FROM, held by identity — the ~4 Hz
   * transport ticks must cost these two comparisons and nothing else. */
  let paintedFavTracks = getState().catalog.favorites.tracks;
  let paintedFavAlbums = getState().catalog.favorites.albums;

  function syncFromStore() {
    const favorites = getState().catalog.favorites;

    if (favorites.tracks !== paintedFavTracks) {
      // Both sets hoisted OUT of the entry loop: two builds, O(1) lookups.
      const previous = new Set(paintedFavTracks);
      const next = new Set(favorites.tracks);
      paintedFavTracks = favorites.tracks;
      let changed = false;
      for (const entry of songEntries) {
        const id = entry.track.id;
        if (previous.has(id) && !next.has(id)) {
          // Left the set -> the row goes NOW (AC 10.3), before any network.
          if (!unfavoritedTracks.has(id)) {
            unfavoritedTracks.add(id);
            changed = true;
          }
        } else if (next.has(id) && unfavoritedTracks.has(id)) {
          // Re-added (the toggle's rollback) -> the row comes back.
          unfavoritedTracks.delete(id);
          changed = true;
        }
      }
      if (changed) paintSongs();
    }

    if (favorites.albums !== paintedFavAlbums) {
      const previous = new Set(paintedFavAlbums);
      const next = new Set(favorites.albums);
      paintedFavAlbums = favorites.albums;
      let changed = false;
      for (const entry of albumEntries) {
        const id = entry.album.id;
        if (previous.has(id) && !next.has(id)) {
          if (!unfavoritedAlbums.has(id)) {
            unfavoritedAlbums.add(id);
            changed = true;
          }
        } else if (next.has(id) && unfavoritedAlbums.has(id)) {
          unfavoritedAlbums.delete(id);
          changed = true;
        }
      }
      if (changed) paintAlbums();
    }
  }

  const unsubscribe = subscribe(() => {
    if (disposed) return;
    syncFromStore();
  });

  /* ── loads ────────────────────────────────────────────────────────────── */

  /**
   * @param {import('@streamymusic/shared').FavoritesResponse['tracks']} sectionData
   * @param {boolean} appendPage
   */
  function applySongs(sectionData, appendPage) {
    if (sectionData === undefined) {
      // ABSENT ≠ EMPTY: this request ASKED for the section, so its absence is
      // a server contract violation — an error card, never "no favorites".
      warn(
        'FavoritesView: the favorites response omitted the "tracks" section it was asked for. ' +
          'Rendering the retriable error, not an empty state (absent ≠ empty).',
        { surface: 'favorites', missingSection: 'tracks' },
      );
      failSongs(appendPage);
      return;
    }
    songEntries = appendPage ? [...songEntries, ...sectionData.items] : [...sectionData.items];
    songsCursor = sectionData.nextCursor;
    if (!appendPage) unfavoritedTracks.clear();
    mergeStoreFavorites(
      'tracks',
      sectionData.items.map((entry) => entry.track.id),
    );
    paintSongs({ announceEmpty: !appendPage });
  }

  /**
   * @param {import('@streamymusic/shared').FavoritesResponse['albums']} sectionData
   * @param {boolean} appendPage
   */
  function applyAlbums(sectionData, appendPage) {
    if (sectionData === undefined) {
      warn(
        'FavoritesView: the favorites response omitted the "albums" section it was asked for. ' +
          'Rendering the retriable error, not an empty state (absent ≠ empty).',
        { surface: 'favorites', missingSection: 'albums' },
      );
      failAlbums(appendPage);
      return;
    }
    albumEntries = appendPage ? [...albumEntries, ...sectionData.items] : [...sectionData.items];
    albumsCursor = sectionData.nextCursor;
    if (!appendPage) unfavoritedAlbums.clear();
    mergeStoreFavorites(
      'albums',
      sectionData.items.map((entry) => entry.album.id),
    );
    paintAlbums({ announceEmpty: !appendPage });
  }

  /** @param {boolean} appendPage */
  function failSongs(appendPage) {
    const retry = ErrorBlock({
      onRetry: () => {
        void loadSongs(appendPage ? { append: true } : {});
      },
      ...(announce === undefined ? {} : { announce }),
    });
    if (appendPage) {
      // A failed NEXT page must not discard the rows already on screen.
      songList.update({ busy: false });
      songsNotice.replaceChildren(retry);
    } else {
      songsBody.replaceChildren(retry);
    }
  }

  /** @param {boolean} appendPage */
  function failAlbums(appendPage) {
    const retry = ErrorBlock({
      onRetry: () => {
        void loadAlbums(appendPage ? { append: true } : {});
      },
      ...(announce === undefined ? {} : { announce }),
    });
    if (appendPage) {
      albumList.update({ busy: false });
      albumsNotice.replaceChildren(retry);
    } else {
      albumsBody.replaceChildren(retry);
    }
  }

  function resetSongsForLoad() {
    songsNotice.replaceChildren();
    songEntries = [];
    songsCursor = null;
    unfavoritedTracks.clear();
    songsCount.textContent = '';
    songsBody.replaceChildren(LoadingBlock({ count: 4, label: 'Loading favorite songs' }));
  }

  function resetAlbumsForLoad() {
    albumsNotice.replaceChildren();
    albumEntries = [];
    albumsCursor = null;
    unfavoritedAlbums.clear();
    albumsCount.textContent = '';
    albumsBody.replaceChildren(
      LoadingBlock({ count: 4, label: 'Loading favorite albums', variant: 'cover' }),
    );
  }

  /**
   * The initial paint: ONE combined request for the first page of BOTH
   * sections (the shape dto.ts provides for exactly this screen). Every
   * later request is section-scoped and `type`-narrowed.
   */
  async function loadAll() {
    const ownSongs = (songsGeneration += 1);
    const ownAlbums = (albumsGeneration += 1);
    songsController?.abort();
    albumsController?.abort();
    const controller = new AbortController();
    songsController = controller;
    albumsController = controller;

    resetSongsForLoad();
    resetAlbumsForLoad();

    try {
      const page = await api.listFavorites({ limit: pageSize, signal: controller.signal });
      if (disposed) return;
      // Each section settles under its OWN generation guard: a section retry
      // issued meanwhile must not be overwritten by this older answer.
      if (ownSongs === songsGeneration) applySongs(page.tracks, false);
      if (ownAlbums === albumsGeneration) applyAlbums(page.albums, false);
    } catch {
      if (disposed) return;
      if (ownSongs === songsGeneration) failSongs(false);
      if (ownAlbums === albumsGeneration) failAlbums(false);
    }
  }

  /** @param {{ append?: boolean }} [opts] */
  async function loadSongs(opts = {}) {
    const appendPage = opts.append === true;
    const own = (songsGeneration += 1);
    songsController?.abort();
    const controller = new AbortController();
    songsController = controller;

    songsNotice.replaceChildren();
    if (!appendPage) resetSongsForLoad();

    try {
      const page = await api.listFavorites({
        // Always `type`-narrowed: per-section paging without it is a 422.
        type: 'track',
        limit: pageSize,
        ...(appendPage && songsCursor !== null ? { cursor: songsCursor } : {}),
        signal: controller.signal,
      });
      if (disposed || own !== songsGeneration) return;
      applySongs(page.tracks, appendPage);
    } catch {
      if (disposed || own !== songsGeneration) return;
      failSongs(appendPage);
    }
  }

  /** @param {{ append?: boolean }} [opts] */
  async function loadAlbums(opts = {}) {
    const appendPage = opts.append === true;
    const own = (albumsGeneration += 1);
    albumsController?.abort();
    const controller = new AbortController();
    albumsController = controller;

    albumsNotice.replaceChildren();
    if (!appendPage) resetAlbumsForLoad();

    try {
      const page = await api.listFavorites({
        type: 'album',
        limit: pageSize,
        ...(appendPage && albumsCursor !== null ? { cursor: albumsCursor } : {}),
        signal: controller.signal,
      });
      if (disposed || own !== albumsGeneration) return;
      applyAlbums(page.albums, appendPage);
    } catch {
      if (disposed || own !== albumsGeneration) return;
      failAlbums(appendPage);
    }
  }

  void loadAll();

  return {
    element: section,
    dispose() {
      // The next settle sees `disposed` and writes nothing into a section the
      // shell has already replaced; the aborts also cancel the transfers, and
      // the unsubscribe drops the store listener so it cannot outlive us.
      disposed = true;
      songsController?.abort();
      albumsController?.abort();
      unsubscribe();
    },
  };
}
